import React, { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { XMarkIcon } from '@heroicons/react/24/outline'

interface Video {
  id: string
  title: string
  labels?: string[]
}

const Videos: React.FC = () => {
  const [selectedVideo, setSelectedVideo] = useState<Video | null>(null)
  const [searchParams, setSearchParams] = useSearchParams()
  const [activeFilter, setActiveFilter] = useState<string>('all')

  const videos: Video[] = [
    {
      id: '1187163207',
      title: 'TrackIEP - Legal Precedent',
      labels: ['marketing'],
    },
    {
      id: '1185027261',
      title: 'TrackIEP - What is it?',
      labels: ['marketing'],
    },
    {
      id: '1180180929',
      title: 'TrackIEP - Product Demo',
      labels: ['marketing'],
    },
    {
      id: '1199940267',
      title: 'TrackIEP - Goals Module',
      labels: ['marketing'],
    },
    {
      id: '1183220040',
      title: 'TrackIEP - Bulk Import',
      labels: ['marketing'],
    },
    {
      id: '1201617316',
      title: 'Tutorial - Logging Accommodations',
      labels: ['tutorial'],
    },
    {
      id: '1202768679',
      title: 'Tutorial - Tracking Goals',
      labels: ['tutorial'],
    },
  ]

  const uniqueLabels = Array.from(
    new Set(videos.flatMap((video) => video.labels || [])),
  ).sort()

  useEffect(() => {
    const filterParam = searchParams.get('filter')
    if (filterParam) {
      const isValidLabel = uniqueLabels.includes(filterParam)
      setActiveFilter(isValidLabel ? filterParam : 'all')
    }
  }, [])

  const filteredVideos =
    activeFilter === 'all'
      ? videos
      : videos.filter(
          (video) => video.labels && video.labels.includes(activeFilter),
        )

  const handleFilterChange = (label: string) => {
    setActiveFilter(label)
    if (label === 'all') {
      searchParams.delete('filter')
    } else {
      searchParams.set('filter', label)
    }
    setSearchParams(searchParams)
  }

  const openVideo = (video: Video) => {
    setSelectedVideo(video)
    document.body.style.overflow = 'hidden'
  }

  const closeVideo = () => {
    setSelectedVideo(null)
    document.body.style.overflow = 'unset'
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            TrackIEP Video Library
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Explore our collection of videos showcasing TrackIEP features,
            tutorials, and success stories.
          </p>
        </div>

        <div className="flex flex-wrap justify-center gap-3 mb-8">
          <button
            onClick={() => handleFilterChange('all')}
            className={`px-6 py-2 rounded-full font-medium transition-all duration-200 ${
              activeFilter === 'all'
                ? 'bg-blue-600 text-white shadow-md'
                : 'bg-white text-gray-700 border border-gray-300 hover:border-blue-400 hover:text-blue-600'
            }`}
          >
            All
          </button>
          {uniqueLabels.map((label) => (
            <button
              key={label}
              onClick={() => handleFilterChange(label)}
              className={`px-6 py-2 rounded-full font-medium transition-all duration-200 capitalize ${
                activeFilter === label
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-white text-gray-700 border border-gray-300 hover:border-blue-400 hover:text-blue-600'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredVideos.map((video) => (
            <div
              key={video.id}
              className="group cursor-pointer"
              onClick={() => openVideo(video)}
            >
              <div className="relative aspect-video bg-gray-900 rounded-lg overflow-hidden shadow-lg transition-transform duration-300 group-hover:scale-105">
                <iframe
                  className="absolute inset-0 w-full h-full pointer-events-none"
                  src={`https://player.vimeo.com/video/${video.id}?title=0&byline=0&portrait=0&controls=0`}
                  frameBorder="0"
                  title={video.title}
                />
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity duration-300 flex items-center justify-center">
                  <svg
                    className="w-16 h-16 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                  </svg>
                </div>
              </div>
              <h3 className="mt-4 text-lg font-semibold text-gray-900">
                {video.title}
              </h3>
            </div>
          ))}
        </div>
      </div>

      {selectedVideo && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-90 p-4"
          onClick={closeVideo}
        >
          <button
            onClick={closeVideo}
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-50"
            aria-label="Close video"
          >
            <XMarkIcon className="w-8 h-8" />
          </button>
          <div
            className="relative w-full max-w-6xl"
            style={{ aspectRatio: '16/9' }}
            onClick={(e) => e.stopPropagation()}
          >
            <iframe
              className="absolute inset-0 w-full h-full rounded-lg"
              src={`https://player.vimeo.com/video/${selectedVideo.id}?autoplay=1&title=0&byline=0&portrait=0`}
              frameBorder="0"
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
              title={selectedVideo.title}
            />
          </div>
        </div>
      )}
    </div>
  )
}

export default Videos
